/**
 * Pure colour utility functions — no Office.js dependency.
 * Ported from the Deckorate Format Tools POC (colorTools.js) and
 * colorTools.js in deckorate-manifest (the former colorUtils.ts).
 *
 * Shared by Colour Tools, Apply Brand, and Brand Check — all three rely on
 * hex normalization and colour distance/luminance math; Colour Tools and
 * Apply Brand additionally rely on theme colour matching and palette
 * generation.
 */

import type { ThemeColors, NearestThemeColor, PaletteCell } from "../types";

// ── Hex normalization ──────────────────────────────────────────────────────────

const NAMED_COLORS: Record<string, string> = {
  black: "000000", white: "FFFFFF", red: "FF0000", green: "008000",
  blue: "0000FF", yellow: "FFFF00", cyan: "00FFFF", magenta: "FF00FF",
  silver: "C0C0C0", gray: "808080", grey: "808080", maroon: "800000",
  olive: "808000", lime: "00FF00", aqua: "00FFFF", teal: "008080",
  navy: "000080", fuchsia: "FF00FF", purple: "800080", orange: "FFA500",
  windowtext: "000000", window: "FFFFFF",
};

export function normalizeHex(hex: string | null | undefined): string | null {
  if (!hex) return null;
  let h = String(hex).trim();
  if (h.startsWith("#")) h = h.slice(1);
  // Strip alpha prefix from AARRGGBB
  if (/^[0-9A-Fa-f]{8}$/.test(h)) return h.slice(2).toUpperCase();
  if (/^[0-9A-Fa-f]{6}$/.test(h)) return h.toUpperCase();
  if (/^[0-9A-Fa-f]{3}$/.test(h)) return (h[0]! + h[0]! + h[1]! + h[1]! + h[2]! + h[2]!).toUpperCase();
  // Handle rgb() notation
  const rgb = /^rgb\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/.exec(h);
  if (rgb) {
    const r = Number.parseInt(rgb[1] ?? "0", 10);
    const g = Number.parseInt(rgb[2] ?? "0", 10);
    const b = Number.parseInt(rgb[3] ?? "0", 10);
    return rgbToHex(r, g, b);
  }
  return NAMED_COLORS[h.toLowerCase()] ?? null;
}

// ── RGB conversion ─────────────────────────────────────────────────────────────

export function hexToRgb(hex: string | null | undefined): { r: number; g: number; b: number } {
  const h = (hex || "").replace(/^#/, "");
  if (h.length < 6) return { r: 0, g: 0, b: 0 };
  return {
    r: Number.parseInt(h.slice(0, 2), 16),
    g: Number.parseInt(h.slice(2, 4), 16),
    b: Number.parseInt(h.slice(4, 6), 16),
  };
}

export function hexToRgbStr(hex: string): string {
  const { r, g, b } = hexToRgb(hex);
  return `${r}, ${g}, ${b}`;
}

export function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return [clamp(r), clamp(g), clamp(b)]
    .map((v) => v.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();
}

export function rgbStrToHex(rgb: string): string | null {
  const parts = rgb.split(",").map((s) => Number.parseInt(s.trim(), 10));
  if (parts.length !== 3 || parts.some((p) => Number.isNaN(p))) return null;
  return rgbToHex(parts[0] ?? 0, parts[1] ?? 0, parts[2] ?? 0);
}

// ── Color math ─────────────────────────────────────────────────────────────────

export function colorDistance(hex1: string, hex2: string): number {
  const a = hexToRgb(hex1);
  const b = hexToRgb(hex2);
  return Math.hypot(a.r - b.r, a.g - b.g, a.b - b.b);
}

export function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

// Alias kept for Apply Brand's existing call sites — same formula as
// relativeLuminance, just the name that feature has always imported.
export const luminance = relativeLuminance;

// Apply Brand's near-white band is deliberately narrower than Colour
// Tools' fill-snapping default (240) — Apply Brand only wants to exclude
// true whites/off-whites from brand-colour remapping, not the lighter
// greys Colour Tools also treats as "near white" when snapping fills to
// theme colours. Named so every Apply Brand call site can share the exact
// same intent instead of repeating an unexplained literal.
export const APPLY_BRAND_NEAR_WHITE_THRESHOLD = 200;

export function isNearWhite(hex: string, threshold = 240): boolean {
  const { r, g, b } = hexToRgb(hex);
  return r > threshold && g > threshold && b > threshold;
}

export function isNearBlack(hex: string, threshold = 40): boolean {
  const { r, g, b } = hexToRgb(hex);
  return r < threshold && g < threshold && b < threshold;
}

// Shared "is this colour too close to plain black/white/grey to be a
// deliberate brand choice" bands — used by both isNeutralHex (raw shape
// fill/line colours) and isDefaultFontColor (text colours) so the two
// checks can't silently drift apart. NEUTRAL_DARK_THRESHOLD matches
// isNearBlack's own default; NEUTRAL_LIGHT_THRESHOLD matches the near-white
// band findNearestThemeFillColor already uses for theme entries.
const NEUTRAL_DARK_THRESHOLD = 40;
const NEUTRAL_LIGHT_THRESHOLD = 220;
// Max spread between a colour's highest and lowest RGB channel below which
// it reads as grey rather than a saturated hue.
const NEUTRAL_MAX_CHANNEL_SPREAD = 18;

function isNeutralRgb(r: number, g: number, b: number): boolean {
  if (r < NEUTRAL_DARK_THRESHOLD && g < NEUTRAL_DARK_THRESHOLD && b < NEUTRAL_DARK_THRESHOLD) return true;
  if (r > NEUTRAL_LIGHT_THRESHOLD && g > NEUTRAL_LIGHT_THRESHOLD && b > NEUTRAL_LIGHT_THRESHOLD) return true;
  return Math.max(r, g, b) - Math.min(r, g, b) < NEUTRAL_MAX_CHANNEL_SPREAD;
}

export function isNeutralHex(hex: string): boolean {
  const h = hex.replace("#", "");
  if (h.length < 6) return true;
  const { r, g, b } = hexToRgb(h);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return true;
  return isNeutralRgb(r, g, b);
}

// Perceived-brightness midpoint of the 0-255 relativeLuminance range —
// values below this read as dark enough to need light contrasting text.
export const MID_LUMINANCE = 128;

export function isDarkish(hex: string): boolean {
  const h = normalizeHex(hex);
  if (!h) return false;
  return relativeLuminance(h) < MID_LUMINANCE;
}

export function isDefaultFontColor(hex: string): boolean {
  const norm = normalizeHex(hex);
  if (!norm) return true;
  const { r, g, b } = hexToRgb(norm);
  return isNeutralRgb(r, g, b);
}

export function blendWithWhite(hex: string, transparency: number): string {
  if (!transparency || transparency <= 0) return hex;
  const pct = transparency <= 1 ? transparency * 100 : transparency;
  if (pct >= 100) return "FFFFFF";
  const { r, g, b } = hexToRgb(hex);
  const t = pct / 100;
  return rgbToHex(r * (1 - t) + 255 * t, g * (1 - t) + 255 * t, b * (1 - t) + 255 * t);
}

// Minimum luminance gap (of 255) between text and fill below which the
// pairing is treated as too low-contrast to read.
const MIN_READABLE_LUMINANCE_GAP = 80;

export function isTextUnreadable(textHex: string, fillHex: string): boolean {
  const t = normalizeHex(textHex);
  const f = normalizeHex(fillHex);
  if (!t || !f) return false;
  return Math.abs(relativeLuminance(t) - relativeLuminance(f)) < MIN_READABLE_LUMINANCE_GAP;
}

/**
 * Find the nearest brand accent/dark colour to the given fill hex.
 * Near-white and near-black fills are excluded from remapping.
 */
export function findNearestBrandColor(
  fillHex: string,
  candidateHexes: string[],
): string | null {
  const norm = normalizeHex(fillHex);
  if (!norm) return null;
  if (isNearWhite(norm, APPLY_BRAND_NEAR_WHITE_THRESHOLD) || isNearBlack(norm)) return null;

  const valid = candidateHexes
    .map((c) => normalizeHex(c))
    .filter((c): c is string => c !== null && !isNearWhite(c, APPLY_BRAND_NEAR_WHITE_THRESHOLD));

  if (valid.length === 0) return null;

  let best = valid[0]!;
  let bestDist = colorDistance(norm, best);
  for (let i = 1; i < valid.length; i++) {
    const dist = colorDistance(norm, valid[i]!);
    if (dist < bestDist) {
      bestDist = dist;
      best = valid[i]!;
    }
  }
  return `#${best}`;
}

// Picks the readable text colour for a given fill: light text on dark fills,
// dark text on light fills.
export function pickContrastText(fillHex: string, lightHex: string, darkHex: string): string {
  return relativeLuminance(fillHex) < MID_LUMINANCE ? lightHex : darkHex;
}

// ── Theme color lookup ─────────────────────────────────────────────────────────

const THEME_KEYS: ReadonlyArray<keyof ThemeColors> = [
  "dk1", "dk2", "lt1", "lt2",
  "accent1", "accent2", "accent3", "accent4", "accent5", "accent6",
  "hlink", "folHlink",
];

function findNearestTheme(
  hex: string,
  themeColors: ThemeColors,
  skipThemeHex?: (themeHex: string) => boolean,
): NearestThemeColor | null {
  let best: NearestThemeColor | null = null;
  for (const key of THEME_KEYS) {
    const themeHex = themeColors[key];
    if (!themeHex) continue;
    if (skipThemeHex?.(themeHex)) continue;
    const dist = colorDistance(hex, themeHex);
    if (!best || dist < best.distance) {
      best = { hex: themeHex, key, distance: dist };
    }
  }
  return best;
}

export function findNearestThemeColor(
  hex: string,
  themeColors: ThemeColors,
): NearestThemeColor | null {
  return findNearestTheme(hex, themeColors);
}

// Near-white band for excluding theme entries from fill-snapping —
// intentionally its own constant rather than reusing NEUTRAL_LIGHT_THRESHOLD
// (they read the same today, but tune Colour Tools' fill-snap band vs Apply
// Brand's neutral-colour detection, and shouldn't move together by accident).
const FILL_SNAP_NEAR_WHITE_THRESHOLD = 220;

export function findNearestThemeFillColor(
  hex: string,
  themeColors: ThemeColors,
): NearestThemeColor | null {
  // Skip near-whites for fill snapping
  return findNearestTheme(hex, themeColors, (themeHex) => isNearWhite(themeHex, FILL_SNAP_NEAR_WHITE_THRESHOLD));
}

// ── Accent helpers ─────────────────────────────────────────────────────────────

export function getAccentHexes(themeColors: ThemeColors): string[] {
  return (["accent1", "accent2", "accent3", "accent4", "accent5", "accent6"] as const)
    .map((k) => themeColors[k])
    .filter((v): v is string => Boolean(v));
}

// ── HSL helpers for OOXML-compatible luminance modulation ─────────────────────
// PowerPoint's theme picker uses HSL-based luminance modulation (ECMA-376
// §20.1.2.3) rather than RGB-linear blending. Only the luminance channel L
// is modified; hue and saturation are preserved, giving pixel-accurate results.
//
//   L' = clamp(L * lumMod + lumOff, 0, 1)
//
// Native PPT rows: Base | Lighter 80% (0.2/0.8) | Lighter 60% (0.4/0.6)
//                       | Lighter 40% (0.6/0.4)  | Darker 25% (0.75/0)
//                       | Darker 50%  (0.5/0)

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn: h = (gn - bn) / d + (gn < bn ? 6 : 0); break;
      case gn: h = (bn - rn) / d + 2; break;
      case bn: h = (rn - gn) / d + 4; break;
    }
    h /= 6;
  }
  return { h, s, l };
}

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  if (s === 0) return { r: l * 255, g: l * 255, b: l * 255 };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return {
    r: hue2rgb(p, q, h + 1 / 3) * 255,
    g: hue2rgb(p, q, h) * 255,
    b: hue2rgb(p, q, h - 1 / 3) * 255,
  };
}

export function applyLumMod(hex: string, lumMod: number, lumOff: number): string {
  const { r, g, b } = hexToRgb(hex);
  const { h, s, l } = rgbToHsl(r, g, b);
  const newL = Math.max(0, Math.min(1, l * lumMod + lumOff));
  const out = hslToRgb(h, s, newL);
  return rgbToHex(out.r, out.g, out.b);
}

// ── Palette generation ─────────────────────────────────────────────────────────
// Column order matches PowerPoint's native color picker: lt1, dk1, lt2, dk2, accents.
// Row order matches OOXML luminance steps: Base, Lighter 80/60/40%, Darker 25/50%.

const PALETTE_KEYS: ReadonlyArray<keyof ThemeColors> = [
  "lt1", "dk1", "lt2", "dk2",
  "accent1", "accent2", "accent3", "accent4", "accent5", "accent6",
];

type LumStep = { label: string; fn: (hex: string) => string };

const LUM_STEPS: ReadonlyArray<LumStep> = [
  { label: "Base",        fn: (hex) => hex },
  { label: "Lighter 80%", fn: (hex) => applyLumMod(hex, 0.2, 0.8) },
  { label: "Lighter 60%", fn: (hex) => applyLumMod(hex, 0.4, 0.6) },
  { label: "Lighter 40%", fn: (hex) => applyLumMod(hex, 0.6, 0.4) },
  { label: "Darker 25%",  fn: (hex) => applyLumMod(hex, 0.75, 0) },
  { label: "Darker 50%",  fn: (hex) => applyLumMod(hex, 0.5, 0) },
];

export function buildPaletteGrid(themeColors: ThemeColors): PaletteCell[][] {
  return LUM_STEPS.map((step, ri) =>
    PALETTE_KEYS.map((key) => {
      const base = themeColors[key] ?? "808080";
      const hex = step.fn(base);
      return { hex, key, row: ri, label: `${key} — ${step.label}` };
    }),
  );
}

// ─── Colour picker conversions ─────────────────────────────────────────────
// Colour conversions + validation for the Customise colour picker (BRD FR-41,
// EC-26). Hex values are 6 digits without "#", upper-case, to match BrandSpec.

export interface Rgb { r: number; g: number; b: number }
export interface Hsl { h: number; s: number; l: number }
export interface Hsv { h: number; s: number; v: number }

const HEX6 = /^[0-9A-F]{6}$/i;

export function clampNumber(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

export function parseHexRgb(hex: string): Rgb {
  const h = hex.replace("#", "");
  if (!HEX6.test(h)) return { r: 0, g: 0, b: 0 };
  return { r: Number.parseInt(h.slice(0, 2), 16), g: Number.parseInt(h.slice(2, 4), 16), b: Number.parseInt(h.slice(4, 6), 16) };
}

export function formatRgbHex({ r, g, b }: Rgb): string {
  return [r, g, b].map((v) => clampNumber(Math.round(v), 0, 255).toString(16).padStart(2, "0")).join("").toUpperCase();
}

export function rgbToHsv({ r, g, b }: Rgb): Hsv {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

export function hsvToRgb({ h, s, v }: Hsv): Rgb {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let rgb: [number, number, number];
  if (h < 60) rgb = [c, x, 0];
  else if (h < 120) rgb = [x, c, 0];
  else if (h < 180) rgb = [0, c, x];
  else if (h < 240) rgb = [0, x, c];
  else if (h < 300) rgb = [x, 0, c];
  else rgb = [c, 0, x];
  return { r: (rgb[0] + m) * 255, g: (rgb[1] + m) * 255, b: (rgb[2] + m) * 255 };
}

export function rgbToHslParts({ r, g, b }: Rgb): Hsl {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = ((gn - bn) / d) % 6;
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  return { h, s, l };
}

export function hslPartsToRgb({ h, s, l }: Hsl): Rgb {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let rgb: [number, number, number];
  if (h < 60) rgb = [c, x, 0];
  else if (h < 120) rgb = [x, c, 0];
  else if (h < 180) rgb = [0, c, x];
  else if (h < 240) rgb = [0, x, c];
  else if (h < 300) rgb = [x, 0, c];
  else rgb = [c, 0, x];
  return { r: (rgb[0] + m) * 255, g: (rgb[1] + m) * 255, b: (rgb[2] + m) * 255 };
}

export type ColourField = "hex" | "r" | "g" | "b" | "h" | "s" | "l";

const NUMERIC_FIELD_RULES: Record<Exclude<ColourField, "hex">, { max: number; message: string }> = {
  r: { max: 255, message: "Enter a value from 0 to 255." },
  g: { max: 255, message: "Enter a value from 0 to 255." },
  b: { max: 255, message: "Enter a value from 0 to 255." },
  h: { max: 360, message: "Enter a hue from 0 to 360." },
  s: { max: 100, message: "Enter a value from 0 to 100." },
  l: { max: 100, message: "Enter a value from 0 to 100." },
};

function validateHexField(value: string): string | null {
  const digits = value.replace(/^#/, "");
  if (!/^[0-9A-Fa-f]*$/.test(digits)) return "Enter a hex code using 0 to 9 and A to F, like #0B5DAA.";
  if (digits.length !== 6) return "Enter a 6-digit hex code, like #0B5DAA.";
  return null;
}

/** Returns the EC-26 message for an invalid value, or null when it is valid. */
export function validateColourField(field: ColourField, raw: string): string | null {
  const value = raw.trim();
  if (field === "hex") return validateHexField(value);
  const { max, message } = NUMERIC_FIELD_RULES[field];
  const n = Number(value);
  const isValid = value !== "" && Number.isFinite(n) && n >= 0 && n <= max;
  return isValid ? null : message;
}

/** True when `hex` is a valid 6-digit hex string (with or without "#"). */
export function isValidHex(hex: string): boolean {
  return HEX6.test(hex.replace(/^#/, ""));
}

/** Normalises user input like "#0b5daa" to "0B5DAA". */
export function toHex6(raw: string): string {
  return raw.replace(/^#/, "").toUpperCase();
}
